import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Globe, Plus, Save, Trash2, ChevronLeft, Percent, X, Pencil, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import CreateSupplierDialog from '@/components/suppliers/CreateSupplierDialog';
import CreateFamilyDialog from '@/components/suppliers/CreateFamilyDialog';
import EditFamilyDialog from '@/components/suppliers/EditFamilyDialog';
import {
  useSuppliersList,
  useSupplierFamilies,
  useCreateOrgSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useUpsertDiscount,
  useDeleteDiscount,
  useDeleteFamily,
  type SupplierRow,
  type FamilyRow,
} from '@/hooks/useSupplierManagement';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { formatNumber } from '@/utils/format';

const Suppliers: React.FC = () => {
  const { currentRole, currentOrganization } = useCurrentOrganization();
  const canManage = currentRole === 'owner' || currentRole === 'admin';
  const canManageDiscounts = canManage; // discount edits limited to owner/admin

  const { data: suppliers = [], isLoading } = useSuppliersList();
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierRow | null>(null);

  const globals = suppliers.filter(s => s.is_global);
  const owns = suppliers.filter(s => !s.is_global);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8 space-y-6">
            {selectedSupplier ? (
              <SupplierDetail
                supplier={selectedSupplier}
                canManageDiscounts={canManageDiscounts}
                canManage={canManage}
                onBack={() => setSelectedSupplier(null)}
                onDeleted={() => setSelectedSupplier(null)}
              />
            ) : (
              <>
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Fornitori</h1>
        <p className="text-muted-foreground text-sm">
          Gestisci i fornitori e gli sconti riservati per <strong>{currentOrganization?.name ?? 'la tua org'}</strong>.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" /> Fornitori globali ({globals.length})
            </CardTitle>
            <CardDescription>Catalogo comune della piattaforma. Puoi attivare i tuoi sconti riservati.</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <SupplierGrid suppliers={globals} onSelect={setSelectedSupplier} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" /> I tuoi fornitori ({owns.length})
            </CardTitle>
            <CardDescription>Fornitori specifici della tua organizzazione.</CardDescription>
          </div>
          {canManage && <CreateSupplierDialog showTrigger />}
        </CardHeader>
        <CardContent>
          {owns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nessun fornitore tuo. {canManage ? 'Puoi aggiungerne uno con il pulsante in alto.' : ''}
            </p>
          ) : (
            <SupplierGrid suppliers={owns} onSelect={setSelectedSupplier} />
          )}
        </CardContent>
      </Card>

      {isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
              </>
            )}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

// ----- Supplier grid -----
const SupplierGrid: React.FC<{ suppliers: SupplierRow[]; onSelect: (s: SupplierRow) => void }> = ({ suppliers, onSelect }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
    {suppliers.map(s => (
      <button
        key={s.id}
        onClick={() => onSelect(s)}
        className="text-left rounded-lg border p-4 hover:border-primary hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold">{s.name}</div>
            <div className="text-xs text-muted-foreground">{s.country_code} · {s.is_active ? 'Attivo' : 'Inattivo'}</div>
          </div>
          {s.is_global ? <Badge variant="secondary">Globale</Badge> : <Badge>Tuo</Badge>}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <Stat label="Famiglie" value={s.families_count} />
          <Stat label="Prodotti" value={s.materials_count} />
          <Stat label="Sconti" value={s.discounts_count} highlight={s.discounts_count > 0} />
        </div>
      </button>
    ))}
  </div>
);

const Stat: React.FC<{ label: string; value: number; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-md bg-muted/30 p-2">
    <div className={`text-lg font-bold ${highlight ? 'text-primary' : ''}`}>{value}</div>
    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
  </div>
);

// CreateSupplierDialog spostato in src/components/suppliers/CreateSupplierDialog.tsx
// (riusabile anche dal CertificationPanel del configuratore).

// ----- Edit supplier dialog -----
interface EditSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: SupplierRow;
  onSave: (patch: { name: string; website?: string | null; contact_email?: string | null; contact_phone?: string | null; default_discount?: string | null; notes?: string | null }) => Promise<void> | void;
  isSaving: boolean;
}

const EditSupplierDialog: React.FC<EditSupplierDialogProps> = ({ open, onOpenChange, supplier, onSave, isSaving }) => {
  const [name, setName] = useState(supplier.name);
  const [website, setWebsite] = useState(supplier.website ?? '');
  const [contactEmail, setContactEmail] = useState(supplier.contact_email ?? '');
  const [contactPhone, setContactPhone] = useState(supplier.contact_phone ?? '');
  const [defaultDiscount, setDefaultDiscount] = useState(supplier.default_discount ?? '');
  const [notes, setNotes] = useState(supplier.notes ?? '');

  // Reset campi quando si apre con un fornitore diverso
  React.useEffect(() => {
    if (open) {
      setName(supplier.name);
      setWebsite(supplier.website ?? '');
      setContactEmail(supplier.contact_email ?? '');
      setContactPhone(supplier.contact_phone ?? '');
      setDefaultDiscount(supplier.default_discount ?? '');
      setNotes(supplier.notes ?? '');
    }
  }, [open, supplier]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onSave({
      name: name.trim(),
      website: website.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      default_discount: defaultDiscount.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifica fornitore</DialogTitle>
          <DialogDescription>Aggiorna i dati di "{supplier.name}".</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="es-name">Nome*</Label>
            <Input id="es-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="es-web">Sito web</Label>
            <Input id="es-web" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="es-email">Email contatto</Label>
              <Input id="es-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="es-phone">Telefono</Label>
              <Input id="es-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="es-disc">Sconto default</Label>
            <Input id="es-disc" value={defaultDiscount} onChange={(e) => setDefaultDiscount(e.target.value)} placeholder='es. "30+5"' />
          </div>
          <div>
            <Label htmlFor="es-notes">Note</Label>
            <Input id="es-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSaving}>
              <Save className="h-4 w-4 mr-1.5" />
              {isSaving ? 'Salvo…' : 'Salva'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ----- Supplier detail with families & discounts -----
const SupplierDetail: React.FC<{
  supplier: SupplierRow;
  canManageDiscounts: boolean;
  canManage: boolean;
  onBack: () => void;
  onDeleted: () => void;
}> = ({ supplier, canManageDiscounts, canManage, onBack, onDeleted }) => {
  const { data: families = [], isLoading } = useSupplierFamilies(supplier.id);
  const updateSupplier = useUpdateSupplier();
  const deleteSupplier = useDeleteSupplier();
  const [editOpen, setEditOpen] = useState(false);

  // Fornitori globali (DryCore-managed) non modificabili dalle org.
  const canEditOrDelete = canManage && !supplier.is_global;

  const handleDelete = () => {
    deleteSupplier.mutate(supplier.id, {
      onSuccess: () => onDeleted(),
    });
  };

  const summary = useMemo(() => ({
    total: families.length,
    withDiscount: families.filter(f => f.discount_id).length,
    withProducts: families.filter(f => f.materials_count > 0).length,
  }), [families]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Tutti i fornitori
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{supplier.name}</h1>
            <p className="text-muted-foreground text-sm">
              {supplier.is_global ? 'Fornitore globale' : 'Tuo fornitore'} · {summary.total} famiglie ·
              {' '}{summary.withDiscount} con sconto attivo · {summary.withProducts} con prodotti
            </p>
          </div>
        </div>
        {canEditOrDelete && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Modifica
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                  Elimina
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare il fornitore "{supplier.name}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Il fornitore verrà rimosso dalle liste e dai filtri. I materiali esistenti che lo
                    riferiscono come testo conservano il riferimento storico ma non avranno più sconti
                    riservati. Azione non reversibile.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Elimina fornitore
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <EditSupplierDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        supplier={supplier}
        onSave={async (patch) => {
          await updateSupplier.mutateAsync({ id: supplier.id, ...patch });
          setEditOpen(false);
        }}
        isSaving={updateSupplier.isPending}
      />

      {!canManageDiscounts && (
        <Alert>
          <AlertDescription>
            Hai accesso in sola lettura. Per modificare gli sconti chiedi al proprietario o admin dell'organizzazione.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" /> Famiglie e sconti riservati
              </CardTitle>
              <CardDescription>
                Le famiglie raggruppano i materiali del fornitore (es. "Lastre Habito", "Profili Gyprofile").
                Lo sconto applicato qui si propaga al <strong>prezzo netto</strong> di tutti i materiali
                associati alla famiglia.
                <br />
                Gli sconti sono cumulativi: <strong>netto = listino × (1 − S1) × (1 − S2)</strong>.
              </CardDescription>
            </div>
            {canManage && (
              <CreateFamilyDialog
                supplierId={supplier.id}
                supplierName={supplier.name}
                showTrigger
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento famiglie…</p>
          ) : families.length === 0 ? (
            <div className="text-center py-6 border-2 border-dashed rounded-md">
              <p className="text-sm text-muted-foreground mb-2">
                Nessuna famiglia configurata per questo fornitore.
              </p>
              {canManage && (
                <CreateFamilyDialog
                  supplierId={supplier.id}
                  supplierName={supplier.name}
                  showTrigger
                />
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codice</TableHead>
                  <TableHead>Famiglia</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Prodotti</TableHead>
                  <TableHead>
                    <span>Sconti cumulativi</span>
                    <div className="font-normal text-[10px] text-muted-foreground mt-0.5">
                      Clicca sui % per modificare, + per aggiungere livello
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Paghi</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {families.map(f => (
                  <FamilyRow
                    key={f.id}
                    family={f}
                    supplierId={supplier.id}
                    canManage={canManageDiscounts}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ----- Family row with editable discount chain (N levels) -----
const MAX_LEVELS = 6;

const FamilyRow: React.FC<{
  family: FamilyRow;
  supplierId: string;
  canManage: boolean;
}> = ({ family, supplierId, canManage }) => {
  const upsert = useUpsertDiscount();
  const remove = useDeleteDiscount();
  const [editFamilyOpen, setEditFamilyOpen] = useState(false);

  // Local edit state: array of strings (so empty inputs don't get coerced to 0)
  const [chain, setChain] = useState<string[]>(
    family.discounts.length > 0 ? family.discounts.map(d => String(d)) : ['']
  );

  React.useEffect(() => {
    setChain(family.discounts.length > 0 ? family.discounts.map(d => String(d)) : ['']);
  }, [family.discounts]);

  const numericChain = chain
    .map(s => parseFloat(s))
    .filter(n => Number.isFinite(n) && n > 0 && n < 100);

  const factor = numericChain.reduce((acc, d) => acc * (1 - d / 100), 1);
  const netPct = factor * 100;
  const hasAnyDiscount = numericChain.length > 0;

  const original = family.discounts.map(d => String(d));
  const dirty = JSON.stringify(original) !== JSON.stringify(chain.filter(s => s !== ''));

  const updateLevel = (i: number, v: string) => {
    setChain(prev => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };

  const addLevel = () => {
    if (chain.length >= MAX_LEVELS) return;
    setChain(prev => [...prev, '']);
  };

  const removeLevel = (i: number) => {
    setChain(prev => prev.length === 1 ? [''] : prev.filter((_, idx) => idx !== i));
  };

  const handleSave = () => {
    upsert.mutate({
      supplier_id: supplierId,
      family_id: family.id,
      discounts: numericChain,
    });
  };

  const handleClear = () => {
    if (!family.discount_id) return;
    if (!confirm(`Rimuovere tutti gli sconti sulla famiglia "${family.name}"?`)) return;
    remove.mutate(family.discount_id);
  };

  return (
    <TableRow>
      <TableCell>
        <button
          type="button"
          onClick={() => canManage && setEditFamilyOpen(true)}
          disabled={!canManage}
          className={
            'inline-flex items-center group ' +
            (canManage ? 'cursor-pointer' : 'cursor-default')
          }
          title={canManage ? 'Modifica famiglia' : undefined}
        >
          <Badge variant="outline" className="font-mono text-xs group-hover:border-primary">
            {family.code}
          </Badge>
          {canManage && (
            <Pencil className="h-3 w-3 ml-1.5 opacity-30 group-hover:opacity-100 text-muted-foreground" />
          )}
        </button>
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => canManage && setEditFamilyOpen(true)}
          disabled={!canManage}
          className={
            'text-left ' + (canManage ? 'hover:underline cursor-pointer' : 'cursor-default')
          }
        >
          <div className="font-medium text-sm">{family.name}</div>
          {family.brand && <div className="text-xs text-muted-foreground">{family.brand}</div>}
        </button>
        {canManage && (
          <EditFamilyDialog
            family={family}
            open={editFamilyOpen}
            onOpenChange={setEditFamilyOpen}
          />
        )}
      </TableCell>
      <TableCell><span className="text-xs capitalize">{family.category ?? '—'}</span></TableCell>
      <TableCell className="text-right text-sm">{family.materials_count}</TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1">
          {canManage && (
            <Pencil
              className="h-3 w-3 text-muted-foreground/70 mr-1"
              aria-label="Modifica gli sconti"
            />
          )}
          {chain.map((v, i) => (
            <div key={i} className="flex items-center gap-0.5">
              {i > 0 && <span className="text-xs text-muted-foreground font-semibold">+</span>}
              <div className="relative">
                <Input
                  type="number" step="0.01" min="0" max="100"
                  value={v}
                  onChange={(e) => updateLevel(i, e.target.value)}
                  disabled={!canManage}
                  className={
                    'w-16 h-8 text-sm pr-5 text-right tabular-nums ' +
                    (canManage
                      ? 'border-primary/30 hover:border-primary/60 focus-visible:ring-primary/40 bg-white'
                      : '')
                  }
                  placeholder="0"
                  title={canManage ? 'Modifica %' : undefined}
                />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">
                  %
                </span>
              </div>
              {canManage && chain.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLevel(i)}
                  className="text-muted-foreground hover:text-destructive p-0.5 rounded hover:bg-destructive/10"
                  title="Rimuovi questo livello"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          {canManage && chain.length < MAX_LEVELS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 gap-1 border-dashed text-muted-foreground hover:text-foreground"
              onClick={addLevel}
              title="Aggiungi un livello di sconto in catena (es. 30 + 5)"
            >
              <Plus className="h-3 w-3" />
              <span className="text-[11px]">livello</span>
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        {hasAnyDiscount ? (
          <span className="font-semibold text-primary">{formatNumber(netPct)}%</span>
        ) : (
          <span className="text-muted-foreground text-sm">listino</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-1 justify-end items-center">
          {canManage && dirty && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-muted-foreground"
                onClick={() => setChain(original.length > 0 ? original : [''])}
                disabled={upsert.isPending}
                title="Annulla modifiche"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="default" onClick={handleSave} disabled={upsert.isPending}>
                <Save className="h-3.5 w-3.5 mr-1" />Salva
              </Button>
            </>
          )}
          {canManage && family.discount_id && !dirty && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClear}
              disabled={remove.isPending}
              title="Rimuovi tutti gli sconti"
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default Suppliers;
